import { memo, useCallback, useEffect, useRef, useState, type ComponentProps } from 'react'
import * as Sentry from '@sentry/electron/renderer'
import { Virtuoso, type VirtuosoHandle } from 'react-virtuoso'

import { MOUNT_ALL_VIEWPORT_BY } from '@renderer/utils/virtualization-mount-all'
import { useSidepanelFindInPageOpen } from '../../atoms'
import { ChatsInProgress, ForYouChatRow } from '../../components/sidepanel/sections/running-chats'
import { SidepanelSectionViewMore } from '../../components/sidepanel/nested/section-view-more'
import { ViewActionCell } from '../../components/view-action-cell'
import { ViewGroupLabel } from '../../components/view-group-label'
import { useViewContext } from '../../view-context'
import { View } from '../../components/view'
import { ForYouViewHeader } from './nested/header'
import { isDailyBriefChat } from '../../components/day-zero-tour/live-stop'
import { useActiveTourTarget } from '../../components/day-zero-tour/tour-state'
import { useForYouViewModel, type ForYouViewContext } from './use-for-you-view-model'
import { forYouListRowKey, type ForYouListRow } from './for-you-list-rows'
import type { StagedAction } from '../../hooks/use-staged-actions'
import { useSidepanelSurface } from '../../profile/use-sidepanel-profile'

const NORMAL_VIEWPORT_BY = { top: 200, bottom: 200 }

const rowTourTarget = (row: ForYouListRow, actionById: ReadonlyMap<string, StagedAction>) =>
  row.kind === 'chat' && isDailyBriefChat(row.chat, actionById) ? 'daily-brief-card' : undefined

const ForYouActionRow = memo(function ForYouActionRow({ action }: { action: StagedAction }) {
  return (
    <div className="pb-[2px]">
      <ViewActionCell action={action} sourceLabel="For You" hoverPreview />
    </div>
  )
})

interface ForYouListItemProps {
  row: ForYouListRow
  actionById: ReadonlyMap<string, StagedAction>
  feedKey: string
  isFetchingNextPage: boolean
  onViewMore: () => void
  archived: boolean
  chatAppearance: ComponentProps<typeof ForYouChatRow>['appearance']
  /** Rows are tagged for the tour's ring only while a mission runs. */
  touring: boolean
}

const ForYouListItem = memo(function ForYouListItem({
  row,
  actionById,
  feedKey,
  isFetchingNextPage,
  onViewMore,
  archived,
  chatAppearance,
  touring,
}: ForYouListItemProps) {
  switch (row.kind) {
    case 'label': {
      return <ViewGroupLabel isFirst={row.isFirst}>{row.label}</ViewGroupLabel>
    }
    case 'in-progress': {
      return (
        <div className="pb-[2px]">
          <ChatsInProgress chats={row.chats} />
        </div>
      )
    }
    case 'chat': {
      return (
        <div className="pb-[2px]" data-tour-section={touring ? rowTourTarget(row, actionById) : undefined}>
          <ForYouChatRow
            chat={row.chat}
            actions={actionById}
            sourceLabel="For You"
            feedKey={feedKey}
            position={row.position}
            archived={archived}
            appearance={chatAppearance}
          />
        </div>
      )
    }
    case 'view-more': {
      return (
        <div className="pb-[2px]">
          <SidepanelSectionViewMore onClick={onViewMore} disabled={isFetchingNextPage} />
        </div>
      )
    }
    case 'action': {
      return <ForYouActionRow action={row.action} />
    }
  }
})

export function ForYouView() {
  const { context } = useViewContext<ForYouViewContext>()
  const model = useForYouViewModel(context)
  const findInPageOpen = useSidepanelFindInPageOpen()
  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(null)
  // The `home` surface's second seam: the To-dos Home's chats read as to-dos here too.
  const chatAppearance = useSidepanelSurface('home') === 'todos' ? 'todo' : 'card'
  const captureScrollElement = useCallback((element: HTMLDivElement | null) => setScrollElement(element), [])

  // The list is virtualized, so a tour heading for a row further down has to bring it into range itself.
  const virtuosoRef = useRef<VirtuosoHandle>(null)
  const tourTarget = useActiveTourTarget()
  const tourRowIndex = tourTarget
    ? model.rows.findIndex((row) => rowTourTarget(row, model.actionById) === tourTarget)
    : -1
  useEffect(() => {
    if (tourRowIndex >= 0) {
      virtuosoRef.current?.scrollToIndex({ index: tourRowIndex, align: 'center', behavior: 'smooth' })
    }
  }, [tourRowIndex, scrollElement])

  const handleViewMore = useCallback(() => {
    void model.fetchNextPage().catch((error) => Sentry.captureException(error))
  }, [model.fetchNextPage])

  const loading = <p className="text-foreground-secondary type-ui-sm px-3 py-1">Loading For You…</p>

  return (
    <View>
      <ForYouViewHeader filter={model.filter} onFilterChange={model.setFilter} />
      <View.Body bottomInset="list" onScrollElement={captureScrollElement}>
        {model.isLoading ? (
          loading
        ) : model.isError ? (
          <p className="text-danger-subtle type-ui-sm px-3 py-1">Couldn’t load For You. Try refreshing.</p>
        ) : model.rows.length === 0 ? (
          <View.Empty>{model.emptyMessage}</View.Empty>
        ) : scrollElement ? (
          <Virtuoso
            ref={virtuosoRef}
            customScrollParent={scrollElement}
            data={model.rows}
            computeItemKey={(_index, row) => forYouListRowKey(row)}
            itemContent={(_index, row) => (
              <ForYouListItem
                row={row}
                actionById={model.actionById}
                feedKey={model.feedKey}
                isFetchingNextPage={model.isFetchingNextPage}
                onViewMore={handleViewMore}
                archived={model.filter === 'archived'}
                chatAppearance={chatAppearance}
                touring={tourTarget !== null}
              />
            )}
            increaseViewportBy={findInPageOpen ? MOUNT_ALL_VIEWPORT_BY : NORMAL_VIEWPORT_BY}
          />
        ) : (
          // Virtuoso cannot mount until the body reports its scroll element.
          loading
        )}
      </View.Body>
    </View>
  )
}
